/**
 * De rustige projectpagina (ontwerp 23 okt 2026, ~/.local/share/ff-scripts/projectpagina-rustig/).
 *
 * Eén focuspunt: de kaart "Volgende stap". Daaronder een stille lijst "Wat er is" (alleen
 * wat er is, met context), en de rest ingeklapt onder "Projectacties". Kop = titel + één
 * subregel (nr · klant · PL), "Zoeken of vragen" en een ⋯-menu. De fasebalk is een dunne lijn.
 *
 * Alle data komt uit ProjectDataContext (+ de flow, briefings en het Teams-kanaal, die de
 * vorige flow-weergave ook al ophaalde). Er komt geen nieuwe backend bij. Alles wat de vorige
 * weergave kon blijft bereikbaar: elk paneel als drawer, het werkvlak van elke fase
 * (StagePanel) als drawer, klantprofiel/dossier als drawer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, Menu, MenuItem,
  TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL, BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import { useUserRole } from '../../../contexts/UserRoleContext';
import { T, Icon, StitchIconBtn, stitchDialogPaper } from '../../ProjectOverview/parts/stitch';
import { useProjectData } from '../../ProjectOverview/ProjectDataContext';
import { teamRollen } from '../../ProjectOverview/parts/teamRollen';
import { haalNaamMap } from '../../ProjectOverview/parts/TeamRegel';
import { FLOW_PHASES, buildToolUrl } from '../../../config/projectFlow';
import StagePanel from '../StagePanel';
import PhaseSettingsDialog from '../PhaseSettingsDialog';
import OpstartWizard from '../OpstartWizard';
import GenResultDialog from '../GenResultDialog';
import ClientPassportCard from '../ClientPassportCard';
import DossierBlok from '../DossierBlok';
import TeamsKanaalRegel, { regelTekst } from '../TeamsKanaalRegel';
import VraagHetProject from '../VraagHetProject';
import FaseLijn from './FaseLijn';
import { huidigeFase, faseWijzigingen, momentopname, herstelVerschil } from './fasen';
import { bepaalVolgendeStap } from './volgendeStap';
import { watErIs } from './watErIs';
import { offerteStand, mailStand, geld } from './projectStand';
import { VolgendeStapKaart, WatErIsLijst, ProjectactiesBlok, ZijKolom, useOnthouden } from './onderdelen';
import { R, knopSx, tekstLinkSx } from './stijl';
import KlantVereistenBlok from '../../../ui-components/KlantVereistenBlok';

const eerste = (v) => (Array.isArray(v) ? v[0] : v) || '';
const CALLSHEET_TOOL = { path: '/callsheets', supportsContext: true, query: { new: '1' } };
const OFFERTE_TOOL = { path: '/offertes', supportsContext: true };

export default function RustigeProjectPagina({
  acties, onOpenPaneel, onOpenDialog, uren, documenten, emails, waarneming, onDocumentenGewijzigd,
  // Wat deze branch kan (main mist het uren-paneel en de postproductieplanner nog).
  metUrenPaneel = true, metPostprodPlanner = true,
  huidigProject,   // () => id van het project dat nú openstaat (uit ProjectOverview)
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role } = useUserRole() || {};
  const isBeheer = role === 'admin';
  const data = useProjectData();
  const {
    id, project, tasks, deliverables, notes, transcripts, quitclaims, draaidagen, callsheets,
    frameioVideos, qaReviews, quoteLines, quoteLinesLoading, quoteLinesError, pendingProposal,
    markProposal, setSelectedTask, loadData, voegTaakToe, notitieLokaalErbij, notitieLokaalWeg,
    takenOnbekend,   // main: takenfetch mislukt → geen "opstarten"/"afronden" op een lege lijst
  } = data;
  const f = project?.fields || {};
  const projectRecId = project?.airtable_id || project?.id || id;

  // ── Flow (fasen) ───────────────────────────────────────────────────────
  // Antwoorden van een vórig project (snel wisselen) tellen niet: elk verzoek onthoudt voor
  // welk project het liep.
  const huidig = useRef(id);
  huidig.current = id;
  const [flow, setFlow] = useState(null);
  const [flowFout, setFlowFout] = useState(null);
  const laadFlow = useCallback(async () => {
    const voor = id;
    try {
      setFlowFout(null);
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${voor}`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      if (huidig.current !== voor) return null;
      setFlow(d);
      return d;
    } catch (e) {
      if (huidig.current === voor) setFlowFout(e.message);
      return null;
    }
  }, [id]);
  useEffect(() => { setFlow(null); if (id) laadFlow(); }, [id, laadFlow]);
  const phases = useMemo(() => flow?.phases || [], [flow]);
  const nu = useMemo(() => huidigeFase(phases), [phases]);

  // ── Klantbriefings (null = onbekend: de regel "briefing ontbreekt" zwijgt dan) ──
  const [briefings, setBriefings] = useState(null);
  const laadBriefings = useCallback(async () => {
    const voor = id;
    let uit = null;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings`);
      uit = r.ok ? ((await r.json())?.briefings || []) : null;
    } catch { uit = null; }
    if (huidig.current === voor) setBriefings(uit);
  }, [projectRecId, id]);
  useEffect(() => { setBriefings(null); if (projectRecId) laadBriefings(); }, [projectRecId, laadBriefings]);

  // ── Teams-kanaal: bestaat het, dan een link in de subregel; de proefstand alleen voor beheer ──
  const [teams, setTeams] = useState(null);
  useEffect(() => {
    if (!projectRecId) return undefined;
    let actueel = true;
    setTeams(null);
    authFetch(`${AI_ENGINE_URL}/api/v1/teams/kanaal/project/${encodeURIComponent(projectRecId)}`)
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (actueel) setTeams(d); }).catch(() => {});
    return () => { actueel = false; };
  }, [projectRecId]);
  const teamsRegel = regelTekst(teams);
  const [teamsOpen, setTeamsOpen] = useState(false);
  // Aanmaken/koppelen mag iedereen in de live-stand; de proefstand is alleen voor beheer.
  const teamsInMenu = !!teamsRegel && teamsRegel.soort !== 'klaar' && (!teamsRegel.proef || isBeheer);
  // ?teamskanaal=1 (knop uit de mail) opent de dialoog alleen als je hem ook via het menu zou zien.
  // De parameter laat TeamsKanaalRegel zelf weghalen (die opent dan meteen de teamkeuze).
  const teamsDeeplink = searchParams.get('teamskanaal') === '1';
  useEffect(() => {
    if (!teamsDeeplink || teams === null) return;
    if (teamsInMenu) { setTeamsOpen(true); return; }
    const next = new URLSearchParams(searchParams);
    next.delete('teamskanaal');
    setSearchParams(next, { replace: true });
  }, [teamsDeeplink, teams, teamsInMenu, searchParams, setSearchParams]);

  // ── Projectleider voor de subregel (id's → namen, zelfde bron als TeamRegel) ──
  const [naamMap, setNaamMap] = useState(null);
  useEffect(() => {
    let actief = true;
    haalNaamMap().then((m) => { if (actief) setNaamMap(m); });
    return () => { actief = false; };
  }, []);
  const pl = (teamRollen(f, naamMap).find((r) => r.rol === 'Projectleider')?.namen || []).join(', ');

  // ── Dialogs & drawers ──────────────────────────────────────────────────
  const [werkvlak, setWerkvlak] = useState(null);      // phase_key in de drawer
  const [klantOpen, setKlantOpen] = useState(false);
  const [fasenOpen, setFasenOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [zoekOpen, setZoekOpen] = useState(false);
  const [meerAnker, setMeerAnker] = useState(null);
  const [toevoegAnker, setToevoegAnker] = useState(null);
  const [notitieOpen, setNotitieOpen] = useState(false);
  const [taakOpen, setTaakOpen] = useState(false);
  const [sleepBestand, setSleepBestand] = useState(null); // gevallen bestand: wat is het?
  const [sleept, setSleept] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [actiesOpen, wisselActies] = useOnthouden('rustigProjectactiesOpen', false);
  const bestandRef = useRef(null);
  const briefingRef = useRef(null);
  const meerKnop = useRef(null);

  // Deeplinks van de vorige flow blijven werken: ?opstart=1 en ?fase=<key>.
  useEffect(() => {
    const opstart = searchParams.get('opstart') === '1';
    const fase = searchParams.get('fase');
    if (!opstart && !fase) return;
    if (fase && !phases.length) return;   // eerst de fasen
    if (opstart) setWizardOpen(true);
    if (fase && phases.some((p) => p.phase_key === fase)) setWerkvlak(fase);
    const next = new URLSearchParams(searchParams);
    next.delete('opstart'); next.delete('fase');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, phases]);

  // ── Afgeleide stand ────────────────────────────────────────────────────
  const offerte = offerteStand(quoteLines, { laden: quoteLinesLoading, fout: quoteLinesError });
  const mails = mailStand(emails);
  const stap = useMemo(() => bepaalVolgendeStap({
    projectStatus: f.Status, klant: eerste(f.KlantnaamString || f.Klantnaam), fasen: phases, huidigeFase: nu?.phase_key, offerte, taken: takenOnbekend ? null : tasks,
    draaidagen, deliverables, briefings, mails, taakvoorstel: pendingProposal,
  }), [f.Status, phases, nu, offerte, tasks, takenOnbekend, draaidagen, deliverables, briefings, mails, pendingProposal]); // eslint-disable-line react-hooks/exhaustive-deps
  const lijst = watErIs({
    fase: nu?.phase_key, offerte, taken: tasks, deliverables, draaidagen, quitclaims, callsheets, notes,
    meetings: transcripts, documenten, mails, frameio: frameioVideos, qa: qaReviews,
  });

  // ── Acties ─────────────────────────────────────────────────────────────
  const opvolgmail = () => {
    const aan = eerste(f.emailCP);
    const naam = eerste(f['Volledige naam Contactpersoon (from Contactpersoon)']).split(' ')[0];
    const titel = f.Projectnaam || f.Projectnr_en_naam || 'het project';
    const onderwerp = `Offerte ${titel}`;
    const tekst = `${naam ? `Hoi ${naam}` : 'Hoi'},\n\nIk wilde even horen hoe jullie tegen de offerte voor ${titel} aankijken. Zijn er nog vragen of punten die we samen moeten doorlopen?\n\nGroet,\n`;
    window.location.href = `mailto:${encodeURIComponent(aan)}?subject=${encodeURIComponent(onderwerp)}&body=${encodeURIComponent(tekst)}`;
  };

  const voerUit = (actie) => {
    if (!actie) return;
    switch (actie.soort) {
      case 'paneel': onOpenPaneel(actie.paneel); break;
      case 'fase': setWerkvlak(actie.fase); break;
      case 'opstart': setWizardOpen(true); break;
      case 'voorstel': markProposal?.('applied'); onOpenDialog('smartTasks'); break;
      case 'briefing': briefingRef.current?.click(); break;
      case 'mail': opvolgmail(); break;
      case 'gripp': acties.gripp?.(); break;
      case 'ververs': acties.ververs(); laadFlow(); laadBriefings(); break;
      case 'taak': {
        const t = (tasks || []).find((x) => x.id === actie.taakId);
        if (t) setSelectedTask(t);
        break;
      }
      case 'tool':
        navigate(buildToolUrl(actie.tool === 'callsheet' ? CALLSHEET_TOOL : OFFERTE_TOOL, project));
        break;
      default: break;
    }
  };

  // Fase wijzigen: de gekozen fase handmatig op 'bezig' (een eerdere handmatige 'bezig' weer
  // automatisch). Omkeerbaar → undo met de vorige overrides.
  // Eén fasewijziging tegelijk: twee snelle keuzes rekenen anders allebei vanuit dezelfde oude
  // stand, en dan staan er twee fasen handmatig op 'bezig'. Wijzigingen (ook ongedaan maken)
  // sluiten achter elkaar aan in een wachtrij; er valt er nooit één stil weg. Tijdens het
  // opslaan zijn de fasekeuzes in het menu uitgeschakeld.
  const [faseBezig, setFaseBezig] = useState(0);
  // Fase-undo hoort bij déze weergave: een andere weergave (vorige flow, tabbladen) laadt de
  // fasen zelf en zou de terugzetting niet zien. Bij unmount verdwijnt het aanbod daarom.
  const gemount = useRef(false);
  const faseUndoToasts = useRef([]);
  useEffect(() => {
    gemount.current = true;
    return () => { gemount.current = false; faseUndoToasts.current.forEach((t) => toast.dismiss(t)); };
  }, []);
  const biedFaseUndoAan = (tekst, stand) => {
    if (!gemount.current) return;   // al weggewisseld: geen aanbod dat toch niet kan
    const t = biedOngedaanAan(tekst, () => {
      if (!gemount.current) throw new Error('je zit in een andere weergave; wijzig de fase daar');
      return metFaseSlot(() => herstelStand(stand));
    });
    faseUndoToasts.current = [...faseUndoToasts.current.slice(-4), t];
  };
  const faseRij = useRef(Promise.resolve());
  const metFaseSlot = (werk) => {
    const beurt = faseRij.current.then(async () => {
      setFaseBezig((n) => n + 1);
      try { return await werk(); } finally { setFaseBezig((n) => n - 1); }
    });
    faseRij.current = beurt.catch(() => {});
    return beurt;   // fouten (bv. van ongedaan maken) komen bij de aanroeper terecht
  };
  const patchFase = (fid, status_override) => authFetch(`${BACKEND_URL}/Api/project-flow/${id}/phases/${fid}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status_override }),
  }).then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); });
  // De handmatige stand van álle fasen (id → override). Terugzetten = precies deze stand
  // herstellen tegen de VERSE fasen, zodat ook een latere wijziging nooit een tweede
  // 'bezig' laat staan.
  const herstelStand = async (stand, { ookNaWisselen = false } = {}) => {
    const vers = await laadFlow();
    // Intussen weggewisseld? Dan niets meer wijzigen: de andere weergave zou het niet zien.
    // Uitzondering: het terugdraaien van een half mislukte wijziging moet altijd door.
    if (!gemount.current && !ookNaWisselen) throw new Error('je zit in een andere weergave; wijzig de fase daar');
    if (!vers?.phases) { setFasenOpen(true); throw new Error('Fasen niet te laden; kijk ze na via Fasen aanpassen'); }
    const uit = await Promise.allSettled(herstelVerschil(vers.phases, stand).map((w) => patchFase(w.id, w.status_override)));
    await laadFlow();
    if (uit.some((u) => u.status === 'rejected')) {
      setFasenOpen(true);
      throw new Error('Terugzetten lukte niet helemaal; kijk de fasen na');
    }
  };
  const maakHuidig = (key) => metFaseSlot(async () => {
    // Altijd vanuit de verse stand rekenen (een vorige wijziging kan net klaar zijn).
    const vers = await laadFlow();
    if (!gemount.current) return;   // intussen weggewisseld: niets meer wijzigen
    if (!vers?.phases) { toast.error('Fasen niet te laden; probeer het zo opnieuw.'); return; }
    const fasenNu = vers.phases;
    const stand = momentopname(fasenNu);
    const wijzigingen = faseWijzigingen(fasenNu, key);
    if (!wijzigingen.length) return;
    const uitkomst = await Promise.allSettled(wijzigingen.map((w) => patchFase(w.id, w.status_override)));
    const mislukt = uitkomst.filter((u) => u.status === 'rejected');
    if (mislukt.length) {
      // Half gelukt = niets: terug naar de stand van vóór de wijziging.
      try {
        await herstelStand(stand, { ookNaWisselen: true });
        toast.error(`Fase wijzigen mislukt: ${mislukt[0].reason?.message || 'onbekende fout'}`);
      } catch {
        toast.error('Fase wijzigen mislukt, en terugzetten lukte niet helemaal. Kijk de fasen na via Fasen aanpassen.');
      }
      return;
    }
    await laadFlow();
    const label = fasenNu.find((p) => p.phase_key === key)?.label || key;
    biedFaseUndoAan(`Huidige fase: ${label}`, stand);
  });
  const automatisch = (seg) => metFaseSlot(async () => {
    const vers = await laadFlow();
    if (!gemount.current) return;   // intussen weggewisseld: niets meer wijzigen
    if (!vers?.phases) { toast.error('Fasen niet te laden; probeer het zo opnieuw.'); return; }
    const stand = momentopname(vers.phases);
    try {
      await patchFase(seg.id, '');
      await laadFlow();
      biedFaseUndoAan(`${seg.label}: weer automatisch`, stand);
    } catch (e) { toast.error(`Opslaan mislukt: ${e.message}`); }
  });

  // Klantbriefing: eerst bewaren bij het project, dan lezen (zoals in het werkvlak).
  const uploadBriefing = async (file) => {
    if (!file || bezig) return;
    setBezig(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const pr = await authFetch(`${BACKEND_URL}/Api/parse-attachment`, { method: 'POST', body: fd });
      if (!pr.ok) throw new Error((await pr.json().catch(() => ({}))).error || 'Kon het document niet lezen');
      const { text } = await pr.json();
      if (!String(text || '').trim()) throw new Error('Geen tekst gevonden in het document');
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tekst: text, bestandsnaam: file.name, bron: 'upload' }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      await laadBriefings();
      const check = d.briefing?.analyse_markdown || '';
      if (d.analyse_fout) toast(`Briefing bewaard. De analyse lukte niet (${d.analyse_fout}); probeer het later in het werkvlak.`, { icon: '⚠️' });
      // Loopt de check nog op de achtergrond (main: analyse_status 'bezig'), dan geen lege dialoog.
      else if (!check || d.briefing?.analyse_status === 'bezig') toast('Briefing bewaard. De check loopt nog; het resultaat staat straks in het werkvlak van de fase.', { icon: '⏳' });
      else setGenResult({ titel: `Briefing-check: ${file.name}`, markdown: check });
      const bid = d.briefing?.id;
      if (bid) {
        biedOngedaanAan(`Briefing ${file.name} bewaard`, async () => {
          const weg = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings/${bid}`, { method: 'DELETE' });
          if (!weg.ok) throw new Error(`HTTP ${weg.status}`);
          await laadBriefings();
        });
      }
    } catch (e) { toast.error(`Briefing opslaan mislukt: ${e.message}`); }
    finally { setBezig(false); }
  };
  const uploadDocument = async (file) => {
    if (!file || bezig) return;
    if (file.size > 150 * 1024 * 1024) { toast.error(`${file.name}: groter dan 150 MB`); return; }
    setBezig(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('fase', 'auto');
      fd.append('naam', file.name.replace(/\.[^.]+$/, ''));
      if (f.Projectnummer) fd.append('projectNumber', String(f.Projectnummer));
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/documents`, { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      onDocumentenGewijzigd?.();
      const did = d.document?.id;
      if (d.bestond_al) toast(`${file.name} stond al bij dit project`, { icon: 'ℹ️' });
      else if (did) {
        biedOngedaanAan(`${file.name} toegevoegd aan Documenten`, async () => {
          const weg = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/documents/${did}`, { method: 'DELETE' });
          if (!weg.ok) throw new Error(`HTTP ${weg.status}`);
          onDocumentenGewijzigd?.();
        });
      } else toast.success(`${file.name} toegevoegd aan Documenten`);
    } catch (e) { toast.error(`${file.name}: ${e.message}`); }
    finally { setBezig(false); }
  };

  const bewaarNotitie = async (tekst) => {
    const schoon = String(tekst || '').trim();
    if (!schoon) return;
    const voor = id;   // een laat antwoord hoort niet in de lijst van een ander project
    // Vraag het blijvend gemounte ProjectOverview welk project nú openstaat (deze pagina kan
    // intussen weg zijn, bv. naar de tabbladen gewisseld); de lokale lijst hoort bij de provider.
    const zelfdeProject = () => (huidigProject ? huidigProject() : huidig.current) === voor;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: projectRecId, title: schoon.split('\n')[0].slice(0, 50) || 'Notitie', notes: schoon, source: 'Manual' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json().catch(() => ({}));
      if (zelfdeProject()) {
        if (d.record && notitieLokaalErbij) notitieLokaalErbij(d.record);
        else loadData?.({ achtergrond: true });
      }
      setNotitieOpen(false);
      const nieuwId = d.record?.id;
      if (nieuwId) {
        biedOngedaanAan('Notitie toegevoegd', async () => {
          const weg = await authFetch(`${BACKEND_URL}/Api/project-notes/${nieuwId}?projectId=${projectRecId}`, { method: 'DELETE' });
          if (!weg.ok) throw new Error(`HTTP ${weg.status}`);
          if (!zelfdeProject()) return;
          if (notitieLokaalWeg) notitieLokaalWeg(nieuwId); else loadData?.({ achtergrond: true });
        });
      } else toast.success('Notitie toegevoegd');
    } catch (e) { toast.error(`Notitie opslaan mislukt: ${e.message}`); }
  };

  const bewaarTaak = async (naam) => {
    const schoon = String(naam || '').trim();
    if (!schoon) return;
    const ok = await voegTaakToe({ naam: schoon });
    if (ok !== false) setTaakOpen(false);   // mislukt: dialoog en tekst blijven staan
  };

  // Overal op de pagina een bestand laten vallen (vervangt de grote dropzone).
  const isBestand = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const sleepProps = {
    onDragOver: (e) => { if (!isBestand(e)) return; e.preventDefault(); if (!sleept) setSleept(true); },
    onDragLeave: (e) => { if (e.currentTarget.contains(e.relatedTarget)) return; setSleept(false); },
    onDrop: (e) => {
      if (!isBestand(e)) return;
      e.preventDefault(); setSleept(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) setSleepBestand(file);
    },
  };

  // ── Kop ────────────────────────────────────────────────────────────────
  const titel = f.Projectnaam || f.Projectnr_en_naam || 'Project';
  const klant = eerste(f.KlantnaamString || f.Klantnaam);
  const subDelen = [
    f.Projectnummer && String(f.Projectnummer),
    klant,
    pl,
    waarneming && `waargenomen door ${waarneming.naar}${waarneming.tot ? ` t/m ${new Date(waarneming.tot).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}` : ''}`,
  ].filter(Boolean);

  const meerItems = [
    { key: 'bewerken', label: 'Bewerken', actie: acties.bewerken },
    { key: 'historie', label: 'Historie', actie: () => acties.historie(meerKnop.current) },
    { key: 'mail', label: 'Planning mailen naar klant', actie: acties.mail },
    acties.gripp && { key: 'gripp', label: 'Openen in Gripp', actie: acties.gripp },
    teamsInMenu && { key: 'teams', label: teamsRegel.proef ? 'Teams-kanaal (proefstand)' : 'Teams-kanaal aanmaken', actie: () => setTeamsOpen(true) },
    { key: 'fasen', label: 'Fasen aanpassen', actie: () => setFasenOpen(true), uit: faseBezig > 0 },
    { key: 'tabs', label: 'Weergave: tabbladen', actie: () => acties.weergave('tabs'), uitleg: 'wordt onthouden' },
    { key: 'vorige', label: 'Weergave: vorige flow', actie: () => acties.weergave('flow'), uitleg: 'wordt onthouden' },
    { key: 'ververs', label: 'Verversen', actie: () => { acties.ververs(); laadFlow(); laadBriefings(); } },
  ].filter(Boolean);

  // ── Projectacties (ingeklapt) ────────────────────────────────────────────
  const meta = FLOW_PHASES[nu?.phase_key] || { tools: [], dialogActions: [] };
  const projectacties = [
    {
      titel: nu ? `Bij ${nu.label}` : 'Bij deze fase',
      items: [
        nu && { key: 'werkvlak', label: `Werkvlak ${nu.label} openen`, actie: () => setWerkvlak(nu.phase_key),
          uitleg: 'Taken, briefing, documenten, dossier en gereedschap van deze fase' },
        ...(meta.dialogActions || []).map((a) => ({ key: a.key, label: a.label, actie: () => onOpenDialog(a.key) })),
        metPostprodPlanner && nu?.phase_key === 'postproductie' && { key: 'postprod', label: 'Postproductie inplannen', actie: () => onOpenDialog('postprodPlanner') },
        ...(meta.tools || []).map((t) => ({ key: t.path + t.label, label: t.label, actie: () => navigate(buildToolUrl(t, project)),
          uitleg: t.supportsContext ? 'Opent met dit project' : 'Opent zonder projectcontext' })),
      ].filter(Boolean),
    },
    {
      titel: 'Project',
      items: [
        { key: 'opstart', label: 'Project opstarten', actie: () => setWizardOpen(true) },
        { key: 'klant', label: 'Klantprofiel en dossier', actie: () => setKlantOpen(true) },
        metUrenPaneel && { key: 'uren', label: 'Uren en inkoop', actie: () => onOpenPaneel('budget') },
        { key: 'offerteregels', label: 'Offerteregels', actie: () => onOpenPaneel('offerteregels') },
        { key: 'script', label: 'Script', actie: () => onOpenPaneel('script'), uitleg: 'Laatste script uit SharePoint, open in Word' },
        { key: 'activiteit', label: 'Activiteit', actie: () => onOpenPaneel('activiteit') },
      ].filter(Boolean),
    },
  ];

  // ── Zijkolom: alleen met inhoud (offerte akkoord, contactpersoon) ──────────
  const contact = eerste(f['Volledige naam Contactpersoon (from Contactpersoon)']);
  const urenRegel = uren?.gekoppeld && uren.begroot != null
    ? `${Math.round(uren.geschreven || 0)} van ${Math.round(uren.begroot)} uur geschreven` : null;
  const zij = [
    offerte.stand === 'akkoord' && {
      titel: 'Offerte',
      regels: [offerte.bedrag ? `${geld(offerte.bedrag)} geaccepteerd` : 'geaccepteerd', urenRegel],
    },
    offerte.stand === 'akkoord' && (contact || klant) && {
      titel: 'Klant',
      regels: [contact ? `Contact: ${contact}` : klant],
      link: { label: 'Klantprofiel en dossier', actie: () => setKlantOpen(true) },
    },
  ];
  const metZij = zij.some(Boolean);

  return (
    <Box {...sleepProps} sx={{ position: 'relative', maxWidth: 1240, mx: 'auto', pb: 6 }} data-testid="rustige-projectpagina">
      <input ref={bestandRef} type="file" hidden onChange={(e) => { uploadDocument(e.target.files?.[0]); e.target.value = ''; }} />
      <input ref={briefingRef} type="file" hidden accept=".pdf,.docx,.pptx,.txt,.md"
        onChange={(e) => { uploadBriefing(e.target.files?.[0]); e.target.value = ''; }} />

      {/* Kop */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, md: 2 } }}>
        <StitchIconBtn onClick={() => navigate('/projects')} title="Terug naar projecten" role="button" tabIndex={0} aria-label="Terug naar projecten"
          onKeyDown={(e) => { if (e.key === 'Enter') navigate('/projects'); }} sx={{ mt: 0.5, color: R.stil }}>
          <Icon name="arrow_back_ios_new" size={18} />
        </StitchIconBtn>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h1" sx={{ fontFamily: T.font, fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.75rem' },
            lineHeight: 1.25, letterSpacing: '-0.01em', color: R.tekst }}>
            {titel}
          </Typography>
          <Box sx={{ mt: 0.75, fontFamily: T.fontBody, fontSize: '0.94rem', color: R.tekst2, display: 'flex', flexWrap: 'wrap',
            rowGap: 0.25, columnGap: { xs: 1.5, sm: 0 }, '& .punt': { display: { xs: 'none', sm: 'inline' } } }}>
            {subDelen.map((d, i) => (
              <Box key={d} component="span" sx={{ whiteSpace: 'nowrap' }}>
                {i > 0 && <Box component="span" className="punt" aria-hidden sx={{ color: R.stiller, mx: 1 }}>·</Box>}
                {d}
              </Box>
            ))}
            {teamsRegel?.soort === 'klaar' && teamsRegel.url && (
              <Box component="span">
                <Box component="span" className="punt" aria-hidden sx={{ color: R.stiller, mx: 1 }}>·</Box>
                <Box component="a" href={teamsRegel.url} target="_blank" rel="noreferrer" title={`${teamsRegel.tekst} · ${teamsRegel.team || ''}`}
                  sx={{ color: 'inherit', textDecoration: 'none', borderBottom: `1px dotted ${R.stiller}`, '&:hover': { color: R.accent } }}>
                  Teams-kanaal
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2.5 }, mt: 0.5 }}>
          <Box component="button" type="button" onClick={() => setZoekOpen(true)}
            title="Zoeken in notities, mails, meetings en Teams, of een vraag stellen"
            sx={{ ...tekstLinkSx, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Icon name="search" size={18} />
            <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Zoeken of vragen</Box>
          </Box>
          <Box ref={meerKnop} sx={{ display: 'inline-flex' }}>
            <StitchIconBtn onClick={(e) => setMeerAnker(e.currentTarget)} title="Meer: bewerken, historie, mail, Gripp, weergave, fasen, verversen"
              role="button" tabIndex={0} aria-label="Meer acties" aria-haspopup="menu"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMeerAnker(e.currentTarget); } }}
              sx={{ color: R.stil }}>
              <Icon name="more_horiz" size={20} />
            </StitchIconBtn>
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={meerAnker} open={!!meerAnker} onClose={() => setMeerAnker(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: '0.75rem', minWidth: 240 } } }}>
        {meerItems.map((it) => (
          <MenuItem key={it.key} disabled={!!it.uit} onClick={() => { setMeerAnker(null); it.actie?.(); }}
            sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', color: T.text, py: 1, display: 'flex', gap: 1 }}>
            {it.label}
            {it.uitleg && <Box component="span" sx={{ color: R.stil, fontSize: '0.78rem', ml: 'auto' }}>{it.uitleg}</Box>}
          </MenuItem>
        ))}
      </Menu>

      {/* Fasebalk */}
      {flowFout ? (
        <Typography sx={{ my: 4, fontFamily: T.fontBody, fontSize: '0.88rem', color: R.stil }}>
          Fasen niet geladen ({flowFout}).{' '}
          <Box component="button" type="button" onClick={laadFlow} sx={tekstLinkSx}>Opnieuw</Box>
        </Typography>
      ) : !flow ? (
        <Box sx={{ my: 4.5, height: 30, display: 'grid', placeItems: 'center' }}><CircularProgress size={18} /></Box>
      ) : (
        <FaseLijn phases={phases} bezig={faseBezig > 0} onWerkvlak={setWerkvlak} onMaakHuidig={maakHuidig}
          onAutomatisch={automatisch} onFasenAanpassen={() => setFasenOpen(true)} />
      )}

      {/* Raster: werk links, rustige zijkolom rechts (alleen met inhoud) */}
      <Box sx={{
        display: 'grid', gap: { xs: 4, md: 5 }, alignItems: 'start',
        // Ontwerpbreedte: het werk maximaal 820px, de zijkolom 300px ernaast.
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: metZij ? 'minmax(0, 820px) 300px' : 'minmax(0, 820px)' },
      }}>
        <Box component="main" sx={{ minWidth: 0 }}>
          <VolgendeStapKaart stap={stap} onActie={voerUit} />
          <KlantVereistenBlok projectId={projectRecId} nummer={f.Projectnummer} variant="project" sx={{ my: 3 }} />
          <WatErIsLijst rijen={lijst.rijen} naslag={lijst.naslag}
            onOpen={(k) => onOpenPaneel(k === 'offerte' ? 'offerteregels' : k)}
            onToevoegen={(e) => setToevoegAnker(e.currentTarget)} />
          <ProjectactiesBlok groepen={projectacties} open={actiesOpen} onWissel={wisselActies} />
        </Box>
        {metZij && <ZijKolom blokken={zij} />}
      </Box>

      {/* + Toevoegen */}
      <Menu anchorEl={toevoegAnker} open={!!toevoegAnker} onClose={() => setToevoegAnker(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: '0.75rem', minWidth: 220 } } }}>
        {[
          { key: 'document', label: 'Document', actie: () => bestandRef.current?.click() },
          { key: 'briefing', label: 'Klantbriefing', actie: () => briefingRef.current?.click() },
          { key: 'notitie', label: 'Notitie', actie: () => setNotitieOpen(true) },
          // Zonder snelle taak-aanmaak (main) direct naar het takenpaneel, dan gaat er geen tekst verloren.
          { key: 'taak', label: 'Taak', actie: () => (voegTaakToe ? setTaakOpen(true) : onOpenPaneel('taken')) },
          { key: 'meeting', label: 'Meeting koppelen', actie: () => onOpenPaneel('meetings') },
        ].map((it) => (
          <MenuItem key={it.key} onClick={() => { setToevoegAnker(null); it.actie(); }}
            sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', color: T.text, py: 1 }}>
            {it.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Sleep-overlay: alleen tijdens het slepen */}
      {sleept && (
        <Box aria-hidden sx={{ position: 'fixed', inset: 0, zIndex: 1300, pointerEvents: 'none',
          bgcolor: 'rgba(246,248,248,0.85)', display: 'grid', placeItems: 'center' }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1.1rem', color: R.accent }}>
            Laat los om toe te voegen aan dit project
          </Typography>
        </Box>
      )}
      {bezig && (
        <Box sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1300,
          bgcolor: R.vlak, borderRadius: '10px', px: 2, py: 1, boxShadow: '0 4px 16px rgba(45,51,53,0.12)',
          display: 'flex', alignItems: 'center', gap: 1, fontFamily: T.fontBody, fontSize: '0.88rem', color: R.tekst2 }}>
          <CircularProgress size={14} /> Bezig met opslaan…
        </Box>
      )}

      {/* Wat is het gevallen bestand? */}
      <Dialog open={!!sleepBestand} onClose={() => setSleepBestand(null)} maxWidth="xs" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
        <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '1.05rem' }}>{sleepBestand?.name}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', color: R.tekst2 }}>
            Bewaren als projectdocument, of als klantbriefing (dan lezen we wat erin staat en wat ontbreekt)?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
          <Box component="button" type="button" sx={tekstLinkSx}
            onClick={() => { const b = sleepBestand; setSleepBestand(null); uploadBriefing(b); }}>
            Klantbriefing
          </Box>
          <Box component="button" type="button" sx={knopSx}
            onClick={() => { const b = sleepBestand; setSleepBestand(null); uploadDocument(b); }}>
            Projectdocument
          </Box>
        </DialogActions>
      </Dialog>

      <InvoerDialog open={notitieOpen} titel="Notitie toevoegen" label="Notitie" meerregelig
        onClose={() => setNotitieOpen(false)} onBewaar={bewaarNotitie} />
      <InvoerDialog open={taakOpen} titel="Taak toevoegen" label="Wat moet er gebeuren?"
        onClose={() => setTaakOpen(false)} onBewaar={bewaarTaak} />

      {/* Zoeken of vragen */}
      <Dialog open={zoekOpen} onClose={() => setZoekOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { ...stitchDialogPaper, alignSelf: 'flex-start', mt: 8 } }}>
        <DialogContent sx={{ pt: 2.5 }}>
          <VraagHetProject projectRecId={projectRecId} />
        </DialogContent>
      </Dialog>

      {/* Teams-kanaal aanmaken / proefstand (beheer) */}
      <Dialog open={teamsOpen && teamsInMenu} onClose={() => setTeamsOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
        <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '1.05rem' }}>Teams-kanaal</DialogTitle>
        <DialogContent><TeamsKanaalRegel projectRecId={projectRecId} /></DialogContent>
      </Dialog>

      {/* Werkvlak van een fase: alles van de vorige flow-weergave, per fase */}
      <Drawer anchor="right" open={!!werkvlak} onClose={() => setWerkvlak(null)}
        PaperProps={{ sx: { width: { xs: '100vw', md: 900 }, maxWidth: '96vw', p: { xs: 2, md: 3 }, bgcolor: '#f6f8f8' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '1.05rem', flex: 1 }}>
            {phases.find((p) => p.phase_key === werkvlak)?.label || 'Fase'}
          </Typography>
          <StitchIconBtn onClick={() => setWerkvlak(null)} title="Sluiten"><Icon name="close" size={20} /></StitchIconBtn>
        </Box>
        {werkvlak && (
          <StagePanel phase={phases.find((p) => p.phase_key === werkvlak) || null} project={project}
            onOpenPaneel={onOpenPaneel} onOpenDialog={onOpenDialog} />
        )}
      </Drawer>

      {/* Klantprofiel en dossier: geen eigen podium op de pagina, wel één klik weg */}
      <Drawer anchor="right" open={klantOpen} onClose={() => setKlantOpen(false)}
        PaperProps={{ sx: { width: { xs: '100vw', md: 640 }, maxWidth: '96vw', p: { xs: 2, md: 3 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '1.05rem', flex: 1 }}>Klantprofiel en dossier</Typography>
          <StitchIconBtn onClick={() => setKlantOpen(false)} title="Sluiten"><Icon name="close" size={20} /></StitchIconBtn>
        </Box>
        {klantOpen && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <ClientPassportCard project={project} clientName={klant} flow={flow} onFlowChanged={laadFlow} />
            <DossierBlok projectRecId={projectRecId} projectNumber={f.Projectnummer}
              sectie={nu?.phase_key === 'productie' ? 'productie' : 'kern'}
              toonVast={['offertefase', 'opstartfase'].includes(nu?.phase_key)} />
          </Box>
        )}
      </Drawer>

      <OpstartWizard open={wizardOpen} onClose={() => setWizardOpen(false)} project={project}
        onOpenDialog={onOpenDialog} onOpenFasen={() => setFasenOpen(true)} onOpenPaneel={onOpenPaneel}
        onDebriefResultaat={setGenResult} />
      <GenResultDialog result={genResult} onClose={() => setGenResult(null)} projectRecId={projectRecId} />
      <PhaseSettingsDialog open={fasenOpen} onClose={() => setFasenOpen(false)} projectRecId={id}
        phases={phases} driftKeys={flow?.template_drift?.missing_phase_keys || []} onChanged={laadFlow} />
    </Box>
  );
}

/** Kleine invoerdialoog (notitie, taak). Enter bewaart; bij meerregelig Cmd/Ctrl+Enter. */
function InvoerDialog({ open, titel, label, meerregelig = false, onClose, onBewaar }) {
  const [tekst, setTekst] = useState('');
  const [bezig, setBezig] = useState(false);
  useEffect(() => { if (open) setTekst(''); }, [open]);
  const bewaar = async () => {
    if (!tekst.trim() || bezig) return;
    setBezig(true);
    try { await onBewaar(tekst); } finally { setBezig(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
      <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '1.05rem' }}>{titel}</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth label={label} value={tekst} onChange={(e) => setTekst(e.target.value)}
          multiline={meerregelig} minRows={meerregelig ? 4 : undefined} sx={{ mt: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (!meerregelig || e.metaKey || e.ctrlKey)) { e.preventDefault(); bewaar(); }
          }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
        <Box component="button" type="button" onClick={onClose} sx={tekstLinkSx}>Annuleren</Box>
        <Box component="button" type="button" onClick={bewaar} disabled={!tekst.trim() || bezig} sx={knopSx}>
          {bezig ? 'Bewaren…' : 'Toevoegen'}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
